import React from "react";
import Layout from "@/components/Layout";
import Disclaimer from "@/components/Disclaimer";
import { CheckCircle2 } from "lucide-react";

const STEPS = [
  { n: 1, title: "Monday review",  body: "Our team checks the 8 official government URLs in our Methodology for any regulatory updates." },
  { n: 2, title: "Mark as 'Under Legal Review'", body: "If a change is detected, the affected jurisdiction is immediately disabled in our system and a yellow status appears in your dashboard." },
  { n: 3, title: "Verify against statute", body: "The updated statute or guidance is read in full and cross-checked against the official source. We do not rely on third-party summaries." },
  { n: 4, title: "Update the database", body: "The database is updated and LastVerifiedDate is refreshed across every relevant record." },
  { n: 5, title: "Notify and re-enable", body: "'Under Legal Review' is cleared. Affected users are notified in their next compliance digest." },
];

export default function LegalReviewProcess() {
  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Trust · Legal Review</div>
        <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl text-[var(--rg-text-strong)] mb-4">How We Verify Our Compliance Data</h1>
        <p className="text-[var(--rg-text)] text-lg leading-relaxed mb-10 max-w-3xl">
          Compliance data is only useful if it&apos;s current and verified. Here is exactly how our weekly review process works.
        </p>

        <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-4">1. Where Our Data Comes From</h2>
        <p className="text-[var(--rg-text)] mb-8">
          Every data point in Regulens traces to an official government source: state revenue departments, attorneys general, or enacted statutes. We do not use third-party summaries as primary sources.
        </p>

        <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-6">2. Our Weekly Review Process</h2>
        <div className="space-y-4 mb-10" data-testid="review-steps">
          {STEPS.map((s) => (
            <div key={s.n} className="flex gap-4 items-start rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[var(--rg-sage)] text-[var(--rg-on-sage)] font-heading font-extrabold flex items-center justify-center">{s.n}</div>
              <div>
                <div className="text-[var(--rg-text-strong)] font-semibold mb-1">{s.title}</div>
                <p className="text-[var(--rg-text)] text-sm leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-4">3. Data Freshness Display</h2>
        <p className="text-[var(--rg-text)] mb-8">
          Every report you receive shows the exact date each data point was last verified and when we next review it. You always know how current your information is.
        </p>

        <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-4">4. What Happens When a Law Changes</h2>
        <p className="text-[var(--rg-text)] mb-10">
          If a state passes new legislation, we immediately disable that jurisdiction in our system and display an Under Legal Review notice until we can verify the new requirements against the official source. Disabled jurisdictions are excluded from score calculations to prevent stale data from affecting your audit.
        </p>

        <Disclaimer />
      </section>
    </Layout>
  );
}
